// ITEM 2, HER DEFECT 4 — **WHERE A CLOSE GOES, ONCE, FOR EVERY SURFACE THAT HAS ONE.**
//
// Her clause: *"a BACK button (house-styled, gold) returning them to wherever they came from (the
// funnel wall, the gate, the room, **the standing return-path pattern**), nobody stuck on the
// page."* — and this house already has that pattern. ITEM 1 built it for the inbox X and wrote the
// whole argument down: `history.length` counts OTHER ORIGINS' entries and `document.referrer` is
// empty on a same-document push, **so the only thing that can honestly say *we pushed this* is a
// value we wrote when we pushed it.**
//
// >>> **SO THIS IS THAT LAW WITH ITS TWO SURFACE-SHAPED CONSTANTS LIFTED INTO PARAMETERS, RATHER
// THAN A SECOND ANSWER TO *WHERE DOES A CLOSE GO* (R28/R29).** <<< Two copies of a `history.back()`
// decision is two chances for one of them to walk a member out of 12th House entirely, and the copy
// that drifts is whichever surface nobody re-walks. `inbox-return.ts` keeps **every export it ever
// had** and delegates its body here (R165: what is retired is the BODY, never the name a reader
// looks for), so ITEM 1's fixture, its battery and its call site are byte-identical.
//
// **THE FALLBACK IS A PARAMETER AND NEVER A DEFAULT, WHICH IS THE ONE THING A SHARED LAW MUST NOT
// GUESS.** The inbox's is `living-room` — the retired chevron's own destination, so an arrival it
// cannot place lands exactly where it landed before. The membership page's is `home`. A law that
// picked one for both would send a stranger who landed on the membership page from an advertisement
// into a room she has no account for.
import type { Page } from "./pages";

export type ReturnPath =
  /** This app pushed the surface onto the stack, so the entry behind it is hers — go back to it,
   *  scroll and all. */
  | { kind: "back"; from: Page }
  /** She arrived some other way. There is no entry of ours behind this one, so a real page is
   *  NAMED — `history.back()` here walks out of 12th House. */
  | { kind: "page"; page: Page };

/**
 * The state object to push when navigating TO a surface that has a close.
 *
 * `page` is where she is standing at that moment. It is RECORDED rather than merely counted because
 * the verdict names it back — a report that says only *go back* cannot be checked against anything.
 */
export function fromState(key: string, page: Page): Record<string, unknown> {
  return { [key]: page };
}

/**
 * What the close should do, given `history.state` and the roster of pages that really exist.
 *
 * `known` is a PARAMETER rather than an import of `PAGE_PATHS`, so a gate drives this over rosters
 * this repo does not have and the law is not merely agreeing with today's page list (R129/R135).
 *
 * **A STAMP NAMING A PAGE NOBODY ROSTERS IS NOT A RETURN.** It is a stale or forged state object,
 * and trusting it calls `history.back()` on an entry that may not be ours — only a real, rostered
 * STRING is a page (R136/R137's coercion lesson: `String(123)` is a plausible-looking key).
 */
export function returnPath(
  state: unknown,
  known: readonly string[],
  key: string,
  fallback: Page,
): ReturnPath {
  const raw = state && typeof state === "object"
    ? (state as Record<string, unknown>)[key]
    : undefined;
  if (typeof raw === "string" && known.includes(raw)) return { kind: "back", from: raw as Page };
  return { kind: "page", page: fallback };
}

/**
 * The whole close, side effects handed in.
 *
 * `back` and `go` are PARAMETERS for R26/R46's reason: a close written as pure side effects on
 * `window` makes a live browser the only instrument that can reach it. Out here a gate RUNS the
 * whole control — not merely the verdict — and reads back which door it took, so *the close calls
 * `back()` only on a stamped arrival* stops being a claim about a line of App.tsx and becomes
 * arithmetic.
 */
export function runReturn(
  state: unknown,
  known: readonly string[],
  key: string,
  fallback: Page,
  { back, go }: { back: () => void; go: (page: Page) => void },
): ReturnPath {
  const answer = returnPath(state, known, key, fallback);
  if (answer.kind === "back") back();
  else go(answer.page);
  return answer;
}

// ---------------------------------------------------------------------------------------------
// HER MEMBERSHIP PAGE'S OWN TWO CONSTANTS.
// ---------------------------------------------------------------------------------------------

/** The key the membership stamp rides under. Written once, read once — never typed at a call site. */
export const MEMBERSHIP_FROM_KEY = "membershipFrom";

/**
 * Where an arrival this law cannot place is sent.
 *
 * **`home` RATHER THAN A ROOM, AND THE REASON IS WHO REACHES THIS PAGE.** A member arrives here
 * from a 402 on a room she was standing in, and the stamp carries her back to it. Everybody else is
 * a stranger off an advertisement or a mailed link with no entry of ours behind her — and sending
 * her into a room she has no account for is the dead end her clause is about. Home is the one page
 * that is true for both.
 */
export const MEMBERSHIP_RETURN_FALLBACK: Page = "home";

/** The state to push when sending somebody to the membership page. */
export function membershipFromState(page: Page): Record<string, unknown> {
  return fromState(MEMBERSHIP_FROM_KEY, page);
}

/**
 * >>> **THE WRITE, WITH ITS WINDOW AS A PARAMETER — SO THE STAMP AND THE READ LIVE IN ONE MODULE.**
 * <<< The page she is standing on at the moment of the push is the ONLY thing that can honestly say
 * where the back button goes: `history.length` counts OTHER ORIGINS' entries and `document.referrer`
 * is empty on a same-document push (ITEM 1's own measured argument), so the only honest answer is a
 * value we write when we push.
 *
 * It lives here rather than at the call site for R26/R46's reason and for R8's: written as bare side
 * effects on `window` in App.tsx, a live browser is the only instrument that can reach it, and the
 * lines are spent in the one file this house ratchets down. A `null` window is a no-op rather than a
 * throw — this runs on a navigation path, and a crash there costs her the page (R19).
 */
export function stampMembershipFrom(win: Window | null, page: Page): boolean {
  if (!win || !win.history || typeof win.history.replaceState !== "function") return false;
  win.history.replaceState(membershipFromState(page), "", win.location?.href);
  return true;
}

export function membershipReturn(state: unknown, known: readonly string[]): ReturnPath {
  return returnPath(state, known, MEMBERSHIP_FROM_KEY, MEMBERSHIP_RETURN_FALLBACK);
}

/**
 * >>> **IT DELEGATES TO `membershipReturn` RATHER THAN RE-DERIVING THE VERDICT (R28/R29), AND R165
 * CAUGHT THE ROUND IN THE ROUND THAT MADE IT.** <<< The first draft called `runReturn` with the two
 * constants again, which left `membershipReturn` exported and **reachable from nothing** — a
 * capability the house has and does not use, which is that census's exact subject. Two spellings of
 * *where does this close go* is the shape ITEM 1's own header refuses, and the copy that drifts is
 * whichever surface nobody re-walks. One verdict, one reader, and the fixture's four clauses about
 * `membershipReturn` are now clauses about the control the page really renders.
 */
export function runMembershipBack(
  state: unknown,
  known: readonly string[],
  { back, go }: { back: () => void; go: (page: Page) => void },
): ReturnPath {
  const answer = membershipReturn(state, known);
  if (answer.kind === "back") back();
  else go(answer.page);
  return answer;
}
